import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, btnSecondary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { useCanAdmin } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Gift, Image as ImageIcon, Loader2 } from "lucide-react";

const empty = { title: "", description: "", image_data: "", terms_and_conditions: "", active: true, priority: 0 };

export default function Offers() {
  const canAdmin = useCanAdmin();
  const canManage = canAdmin("offers");
  const [offers, setOffers] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/offers").then((r) => setOffers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setShow(true); };
  const openEdit = (o) => { setForm({ ...empty, ...o }); setEditId(o.id); setShow(true); };

  const pickImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Image too large — keep it under 500 KB"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, image_data: reader.result }));
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/offers/${editId}`, form);
      else await api.post("/offers", form);
      toast.success(editId ? "Offer updated" : "Offer added");
      setShow(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete offer "${o.title}"?`)) return;
    try { await api.delete(`/offers/${o.id}`); toast.success("Offer removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="offers-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Gift size={24} className="text-emerald-600" /> Offers</h1>
          <p className="text-sm text-slate-500 mt-1">Publish promotional offers. Active ones show up under the <span className="font-medium text-slate-700">Offers</span> tab for every logged-in user.</p>
        </div>
        {canManage && <button data-testid="add-offer-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> Add Offer</button>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>
      ) : offers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500 text-sm">No offers yet. Add one to promote it inside the app.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {offers.map((o) => (
            <div key={o.id} data-testid={`offer-card-${o.id}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm flex flex-col">
              <div className="aspect-[16/9] bg-slate-100 flex items-center justify-center overflow-hidden">
                {o.image_data ? <img src={o.image_data} alt={o.title} className="w-full h-full object-cover" /> : <ImageIcon size={36} className="text-slate-300" />}
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-slate-900" data-testid={`offer-title-${o.id}`}>{o.title}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${o.active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{o.active ? "Active" : "Inactive"}</span>
                </div>
                {o.description && <div className="text-sm text-slate-600 whitespace-pre-line line-clamp-4">{o.description}</div>}
                {o.terms_and_conditions && <details className="text-xs text-slate-500 mt-1"><summary className="cursor-pointer font-medium text-slate-600">Terms & Conditions</summary><div className="whitespace-pre-line pt-1">{o.terms_and_conditions}</div></details>}
                {canManage && (
                  <div className="flex justify-end gap-1 mt-auto pt-2 border-t border-slate-100">
                    <button data-testid={`edit-offer-${o.id}`} onClick={() => openEdit(o)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                    <button data-testid={`delete-offer-${o.id}`} onClick={() => del(o)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Offer" : "Add Offer"} testId="offer-modal">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Title *</label>
            <input data-testid="offer-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="e.g. Diwali Festive Cashback" />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea data-testid="offer-description-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="Short pitch shown on the offer card…" />
          </div>
          <div>
            <label className={labelCls}>Offer Image (≤ 500 KB, PNG/JPG)</label>
            <div className="flex items-start gap-3">
              <div className="w-40 aspect-[16/9] rounded-lg bg-slate-100 border border-dashed border-slate-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                {form.image_data ? <img src={form.image_data} alt="preview" className="w-full h-full object-cover" data-testid="offer-image-preview" /> : <ImageIcon size={22} className="text-slate-300" />}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer">
                  <ImageIcon size={14} /> Choose image
                  <input data-testid="offer-image-input" type="file" accept="image/*" className="hidden" onChange={pickImage} />
                </label>
                {form.image_data && <button type="button" data-testid="offer-image-remove" onClick={() => setForm({ ...form, image_data: "" })} className="ml-2 text-xs text-rose-600 hover:underline">Remove</button>}
                <p className="text-xs text-slate-500">The image is embedded directly into the offer — users see it inline, not as a download.</p>
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Terms & Conditions</label>
            <textarea data-testid="offer-tnc-input" rows={4} value={form.terms_and_conditions} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} className={inputCls} placeholder="Eligibility, validity dates, exclusions…" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="offer-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active</label>
            <label className="flex items-center gap-2 text-sm text-slate-700">Priority <input data-testid="offer-priority-input" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) || 0 })} className={`${inputCls} w-24`} /></label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className={btnSecondary}>Cancel</button>
            <button data-testid="submit-offer-button" type="submit" disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : null} {editId ? "Save" : "Add Offer"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
